// Docs: FEATURES.md "Keyboard shortcuts"; IMPLEMENTATION.md §3.4.
// Studio-shell ⌘K palette: word search + commands in one input.
//   - typing searches words (existing /words/search endpoint, debounced)
//   - Enter on a word opens its card on the Wörter page (inspector on
//     lg+, modal below) — the list itself stays unfiltered
//   - commands: üben starten, tag planen, lektion erstellen, navigation targets
//   - no word match → the query becomes a "search in wörter" action
// Opens via ⌘K / Ctrl+K or the rail button ('palette:toggle'); Esc closes.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { wordsAPI, preferencesAPI } from '../services/api';
import { queryKeys } from '../queryKeys';
import { useNextLesson } from '../hooks/useNextLesson';
import { navItems } from '../utils/navItems';
import { creatorPath } from '../utils/lessonRoutes';
import NavIcon from './NavIcons';
import WordHeaderLine from './WordHeaderLine';
import { trackEvent } from '../analytics';

const DEBOUNCE_MS = 150;
const WORD_LIMIT = 6;

const CommandPalette = () => {
  const navigate = useNavigate();
  const nextLesson = useNextLesson();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  // Open/close wiring -------------------------------------------------------
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => {
          if (!v) trackEvent('command_palette_open', { via: 'keyboard' });
          return !v;
        });
      }
    };
    const onToggle = () => setOpen((v) => !v);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('palette:toggle', onToggle);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('palette:toggle', onToggle);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setSelected(0);
      // Focus after the overlay is in the tree.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Word search -------------------------------------------------------------
  // "Externe Suche" preference gates the »…« nachschlagen command.
  const { data: prefs } = useQuery({
    queryKey: queryKeys.preferences.all,
    queryFn: preferencesAPI.getPreferences,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const externalSearch = prefs?.external_search_enabled !== false;
  const { data: searchResult } = useQuery({
    queryKey: queryKeys.words.search({ palette: debounced }),
    queryFn: () => wordsAPI.searchWords({ search: debounced, limit: WORD_LIMIT }),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });
  const words = debounced.length >= 2 ? searchResult?.page?.items || [] : [];

  // Commands ----------------------------------------------------------------
  const close = useCallback(() => setOpen(false), []);

  const commands = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [
      {
        id: 'practice',
        label:
          nextLesson.mode === 'plan'
            ? 'tag planen'
            : nextLesson.mode === 'setup'
              ? 'training einrichten'
            : nextLesson.mode === 'done'
              ? 'üben — bonus-lektion'
              : nextLesson.mode === 'unavailable'
                ? 'üben'
                : nextLesson.sidetrack
                  ? `üben — nebenspur ${nextLesson.sidetrack.label}`
                  : `üben starten — ${nextLesson.lesson?.name || 'nächste lektion'}`,
        icon: nextLesson.mode === 'plan' ? 'kalender' : 'play',
        kbd: '⌘↵',
        keywords: 'üben starten weiter lektion planen',
        run: () => nextLesson.act(),
      },
      {
        id: 'create-lesson',
        label: 'lektion erstellen',
        icon: 'ueben',
        keywords: 'lektion drill erstellen neu eigene übung',
        run: () => navigate(creatorPath()),
      },
      ...(q.length >= 2
        ? [
            {
              id: 'search-words',
              label: `»${query.trim()}« in wörtern suchen`,
              icon: 'suche',
              keywords: '',
              alwaysMatch: true,
              run: () => navigate(`/words?q=${encodeURIComponent(query.trim())}`),
            },
            ...(externalSearch ? [{
              // Word NOT in the list yet → dictionary lookup (FEATURES.md
              // "Nachschlagen"). Offered alongside the search fallback unless
              // the "Externe Suche" preference is off.
              id: 'lookup-word',
              label: `»${query.trim()}« nachschlagen`,
              icon: 'suche',
              keywords: '',
              alwaysMatch: true,
              run: () => navigate(`/words/lookup?q=${encodeURIComponent(query.trim())}`),
            }] : []),
          ]
        : []),
      ...navItems.map((item) => ({
        id: `nav-${item.path}`,
        label: `gehe zu ${item.label}`,
        icon: item.icon,
        keywords: `gehe zu ${item.label}`,
        run: () => navigate(item.path),
      })),
      {
        id: 'nav-profile',
        label: 'gehe zu profil',
        icon: 'profil',
        keywords: 'gehe zu profil konto abmelden',
        run: () => navigate('/profile'),
      },
    ];
    if (!q) return all;
    return all.filter((c) => c.alwaysMatch || c.keywords.toLowerCase().includes(q));
  }, [query, nextLesson, navigate, externalSearch]);

  // Combined selectable list: words first, then commands.
  const entries = useMemo(
    () => [
      ...words.map((w) => ({
        kind: 'word',
        id: `word-${w.id}`,
        word: w,
        // Open the card (inspector on lg+, modal below) — deliberately NOT
        // via ?q=, so the word list stays unfiltered.
        run: () => navigate('/words', { state: { inspectWordId: w.id, inspectVia: 'command_palette' } }),
      })),
      ...commands.map((c) => ({ kind: 'command', id: c.id, command: c, run: c.run })),
    ],
    [words, commands, navigate],
  );

  useEffect(() => {
    setSelected(0);
  }, [debounced, entries.length]);

  const runEntry = (entry) => {
    trackEvent('command_palette_run', { kind: entry.kind, id: entry.id });
    close();
    entry.run();
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, entries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      // ⌘↵ always fires the practice command, whatever is selected.
      e.preventDefault();
      const practice = entries.find((entry) => entry.id === 'practice');
      if (practice) runEntry(practice);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (entries[selected]) runEntry(entries[selected]);
    }
  };

  if (!open) return null;

  const wordEntries = entries.filter((e) => e.kind === 'word');
  const commandEntries = entries.filter((e) => e.kind === 'command');

  return (
    <div
      className="fixed inset-0 z-[80] bg-[var(--ink-900)]/40 hidden md:block"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Suchen & Befehle"
        className="w-[520px] max-w-[92vw] mx-auto mt-[9dvh] bg-white border-2 border-[var(--ink-900)] shadow-[8px_8px_0_rgba(26,26,26,0.25)]"
      >
        <div className="flex items-center gap-2.5 px-4 py-3 border-b-2 border-[var(--ink-900)]">
          <NavIcon name="suche" size={16} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="wort suchen oder befehl …"
            className="flex-1 text-base outline-none bg-transparent placeholder:text-[var(--ink-400)]"
            data-testid="palette-input"
          />
          <kbd className="text-[10px] font-mono border border-[var(--rule)] px-1.5 py-0.5 text-[var(--ink-500)]">
            esc
          </kbd>
        </div>

        <div className="max-h-[52dvh] overflow-y-auto py-1">
          {wordEntries.length > 0 && (
            <p className="px-4 pt-2 pb-1 text-[9px] uppercase tracking-[0.12em] text-[var(--ink-500)]">
              Wörter
            </p>
          )}
          {wordEntries.map((entry) => {
            const idx = entries.indexOf(entry);
            const w = entry.word;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => runEntry(entry)}
                onMouseEnter={() => setSelected(idx)}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm ${
                  idx === selected ? 'bg-[var(--cream)]' : ''
                }`}
              >
                <WordHeaderLine
                  word={w}
                  includeExamples={false}
                  germanClassName="text-sm font-semibold text-[var(--ink-900)]"
                  className="flex items-center gap-1 min-w-0"
                />
                {w.translation && (
                  <span className="text-xs text-[var(--ink-500)] truncate">
                    {w.translation}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-[var(--ink-500)]">
                  {idx === selected ? 'öffnen ↵' : ''}
                </span>
              </button>
            );
          })}

          {commandEntries.length > 0 && (
            <p className="px-4 pt-2 pb-1 text-[9px] uppercase tracking-[0.12em] text-[var(--ink-500)]">
              Befehle
            </p>
          )}
          {commandEntries.map((entry) => {
            const idx = entries.indexOf(entry);
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => runEntry(entry)}
                onMouseEnter={() => setSelected(idx)}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm text-[var(--ink-900)] ${
                  idx === selected ? 'bg-[var(--cream)]' : ''
                }`}
              >
                <NavIcon name={entry.command.icon} size={15} />
                <span className="flex-1 text-left">{entry.command.label}</span>
                {entry.command.kbd && (
                  <kbd className="text-[10px] font-mono text-[var(--ink-500)]">
                    {entry.command.kbd}
                  </kbd>
                )}
              </button>
            );
          })}

          {entries.length === 0 && (
            <p className="px-4 py-4 text-sm text-[var(--ink-500)]">keine treffer.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
